import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Award, Users, BookOpen, Heart } from 'lucide-react';
import { profileData, stats, expertise } from '../data/mock';
import GlassCard from '../components/GlassCard';
import NeonText from '../components/NeonText';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-black">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 1px, transparent 1px, transparent 7.6923%), repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)',
                backgroundSize: '100% 100%',
                opacity: 0.14
              }}
            />
          </div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Text Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block px-6 py-2 mb-8 border border-white border-opacity-25 bg-white bg-opacity-10">
                <p className="text-white text-sm tracking-wide">{profileData.motto}</p>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                {profileData.name}
              </h1>
              
              <p className="text-2xl text-white text-opacity-85 mb-8">
                {profileData.tagline}
              </p>
              
              <div className="mb-4">
                <p className="text-xl text-[#00FFD1] font-medium mb-2">Current Position:</p>
                <p className="text-lg text-white text-opacity-85">{profileData.designation}</p>
                <p className="text-lg text-white text-opacity-70">{profileData.institution}</p>
              </div>
              
              <div className="mb-8 pt-4 border-t border-white border-opacity-25">
                <p className="text-sm text-white text-opacity-60 mb-1">Previously:</p>
                <p className="text-white text-opacity-70">{profileData.previousDesignation}</p>
                <p className="text-white text-opacity-60">{profileData.previousInstitution}</p>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/about')}
                  className="px-8 py-4 bg-[#00FFD1] text-black font-medium text-lg flex items-center gap-3 transition-all duration-400 hover:bg-white"
                >
                  Explore Journey
                  <ArrowRight size={20} />
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate('/contact')}
                  className="px-8 py-4 bg-white bg-opacity-10 text-white font-medium text-lg transition-all duration-400 hover:bg-white hover:text-black"
                >
                  Get in Touch
                </motion.button>
              </div>
            </motion.div>

            {/* Right Side - Photo */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                {/* Decorative border */}
                <div className="absolute -inset-4 border-2 border-[#00FFD1] opacity-30" />
                
                {/* Image container */}
                <div className="relative overflow-hidden bg-[#121212]">
                  <img
                    src={profileData.photo}
                    alt="Dr. M D Samudri"
                    className="w-full h-auto object-cover"
                  />
                  
                  {/* Gradient overlay at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
                </div>
                
                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-20 h-20 border-t-4 border-r-4 border-[#00FFD1]" />
                <div className="absolute bottom-0 left-0 w-20 h-20 border-b-4 border-l-4 border-[#00FFD1]" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section with Glass Morphism */}
      <section className="py-20 bg-[#121212] relative overflow-hidden">
        {/* Animated background glow */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00FFD1] rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00FFD1] rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="text-center" neonBorder neonColor="#00FFD1">
                  <h3 className="text-5xl font-bold mb-2">
                    <NeonText>{stat.value}</NeonText>
                  </h3>
                  <p className="text-white text-opacity-70 text-lg">{stat.label}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-bold text-white mb-6">
              <NeonText>Areas of Expertise</NeonText>
            </h2>
            <p className="text-xl text-white text-opacity-70 max-w-3xl mx-auto">
              Pioneering innovations in Ayurvedic surgery and holistic healing
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertise.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard neonBorder neonColor="#00FFD1">
                  <div className="w-16 h-16 bg-[#00FFD1] bg-opacity-20 flex items-center justify-center mb-6">
                    {item.icon === 'Syringe' && <Award className="text-[#00FFD1]" size={32} />}
                    {item.icon === 'Microscope' && <BookOpen className="text-[#00FFD1]" size={32} />}
                    {item.icon === 'Stethoscope' && <Heart className="text-[#00FFD1]" size={32} />}
                    {item.icon === 'Heart' && <Heart className="text-[#00FFD1]" size={32} />}
                    {item.icon === 'Users' && <Users className="text-[#00FFD1]" size={32} />}
                    {item.icon === 'Shield' && <Award className="text-[#00FFD1]" size={32} />}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                  <p className="text-white text-opacity-70 leading-relaxed">{item.description}</p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-bold text-white mb-8">
              Committed to Excellence in Healing
            </h2>
            <p className="text-xl text-white text-opacity-70 mb-12">
              With over 15 years of experience in Ayurvedic surgery, research, and humanitarian service, 
              Dr. Samudri continues to push boundaries in medical innovation and compassionate care.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/contact')}
              className="px-10 py-5 bg-[#00FFD1] text-black font-medium text-lg flex items-center gap-3 mx-auto transition-all duration-400 hover:bg-white"
            >
              Schedule Consultation
              <ArrowRight size={20} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;